import { Link } from "@inertiajs/react";
import { ArrowLeft } from "lucide-react";
import InertiaLink from "@/components/buttons/InertiaLink";
import Button from "@/components/buttons/Button";
import KebabMenu, { KebabMenuItem } from "@/components/menus/KebabMenu";
import ArtworkSlot, { ArtworkSlotProps } from "@/components/ArtworkSlot";
import Tooltip from "@/components/Tooltip";

interface PageHeaderProps {
  title: string;
  backLink?: {
    href: string;
    text: string;
  };
  primaryAction?:
    | { text: string; href: string; disabled?: boolean; tooltip?: React.ReactNode }
    | { text: string; onClick: () => void; disabled?: boolean; tooltip?: React.ReactNode };
  secondaryAction?: {
    href: string;
    text: string;
  };
  tertiaryAction?: {
    href: string;
    text: string;
  };
  deleteAction?: {
    onClick: () => void;
    text: string;
  };
  artwork?: ArtworkSlotProps;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  backLink,
  title,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  deleteAction,
  artwork,
}) => {
  const additionalActionCount = [
    secondaryAction,
    tertiaryAction,
    deleteAction,
  ].filter(Boolean).length;
  const hasAnyActions = primaryAction || additionalActionCount > 0;

  const kebabMenuItems: KebabMenuItem[] = [];
  if (secondaryAction) {
    kebabMenuItems.push({ text: secondaryAction.text, href: secondaryAction.href });
  }
  if (tertiaryAction) {
    kebabMenuItems.push({ text: tertiaryAction.text, href: tertiaryAction.href });
  }
  if (deleteAction) {
    kebabMenuItems.push({
      text: deleteAction.text,
      onClick: deleteAction.onClick,
      variant: "danger",
    });
  }

  const renderPrimaryAction = (className?: string) => {
    if (!primaryAction) return null;

    const action =
      "onClick" in primaryAction ? (
        <Button
          text={primaryAction.text}
          type="primary"
          htmlButtonType="button"
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          className={className}
        />
      ) : (
        <InertiaLink
          href={primaryAction.href}
          type="primary"
          text={primaryAction.text}
          disabled={primaryAction.disabled}
          className={className}
        />
      );

    if (!primaryAction.tooltip) return action;

    return (
      <Tooltip content={primaryAction.tooltip}>
        <span tabIndex={0} className={`inline-flex ${className ?? ""}`.trim()}>
          {action}
        </span>
      </Tooltip>
    );
  };

  const showSecondaryInline = Boolean(
    secondaryAction && !tertiaryAction && !deleteAction,
  );

  return (
    <div className="flex w-full flex-col gap-5">
      {backLink && (
        <Link href={backLink.href}>
          <div className="flex flex-row gap-2">
            <ArrowLeft />
            {backLink.text}
          </div>
        </Link>
      )}
      <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <div className="flex min-w-0 flex-1 flex-row items-center gap-3 md:gap-4">
          {artwork && <ArtworkSlot {...artwork} />}
          <h1 className="min-w-0 font-serif text-2xl font-bold break-words lg:text-4xl">
            {title}
          </h1>
        </div>
        {hasAnyActions && (
          <>
            {/* Desktop: Show all actions in a row */}
            <div className="hidden shrink-0 gap-4 md:flex">
              {deleteAction && (
                <Button
                  text={deleteAction.text}
                  type="danger"
                  htmlButtonType="button"
                  onClick={deleteAction.onClick}
                />
              )}
              {tertiaryAction && (
                <InertiaLink
                  href={tertiaryAction.href}
                  type="tertiary"
                  text={tertiaryAction.text}
                />
              )}
              {secondaryAction && (
                <InertiaLink
                  href={secondaryAction.href}
                  type="secondary"
                  text={secondaryAction.text}
                />
              )}
              {renderPrimaryAction()}
            </div>

            {/* Mobile: actions fill their own row; anything past a lone secondary goes in the kebab */}
            <div className="flex shrink-0 items-center gap-3 md:hidden">
              {showSecondaryInline && secondaryAction && (
                <InertiaLink
                  href={secondaryAction.href}
                  type="secondary"
                  text={secondaryAction.text}
                  className="w-full"
                />
              )}
              {renderPrimaryAction("w-full")}
              {!showSecondaryInline && additionalActionCount > 0 && (
                <span className="shrink-0">
                  <KebabMenu items={kebabMenuItems} />
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
